import { SurgeFilledIcon } from 'cokitchen-icons';
import DealCard from 'components/deal-card';
import { Link, useSearchParams } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import { ENUM_DEAL_STATUS } from 'utils/enums';
import { ReactComponent as DealsEmptyState } from 'assets/icons/deals-empty-state.svg';
import { ROUTE_KEYS } from 'utils/constants';
import PerformanceOverviewModal from 'components/modals/performance-overview-modal';
import { apiService } from 'services/base.service';
import { handleApiError } from 'utils/functions';
import useDashboardContext from 'contexts/dashboard.context';
import { DealType } from 'models/deals.model';
import PageLoader from 'components/base/page-loader';
import InfiniteScroll from 'react-infinite-scroll-component';

const statusArr = [
  ENUM_DEAL_STATUS.All,
  ENUM_DEAL_STATUS.Active,
  ENUM_DEAL_STATUS.Expired,
  ENUM_DEAL_STATUS.Upcoming
];

interface DealState {
  loading: boolean;
  deals: DealType[];
  page: number;
  totalDeals: number;
}

const Deals = () => {
  const [dealStatus, setDealStatus] = useSearchParams();
  const dealStatusValue = (dealStatus.get('dealStatus') ??
    ENUM_DEAL_STATUS.All.toLowerCase()) as string;
  const [openPerformaceOverviewModal, setOpenPerformaceOverviewModal] = useState(false);
  const [dealState, setDealState] = useState<DealState>({
    loading: false,
    deals: [],
    page: 1,
    totalDeals: 0
  });
  const { searchValue, setSearchValue, activeCokitchen } = useDashboardContext();

  const fetchDeals = useCallback(
    async (page: number, shouldReset = false) => {
      try {
        setDealState((prevState) => ({
          ...prevState,
          loading: shouldReset
        }));
        const res = await apiService.get(`marketing/deal`, {
          params: {
            ...(dealStatusValue !== ENUM_DEAL_STATUS.All.toLowerCase() && {
              status: dealStatusValue
            }),
            ...(searchValue && { search: searchValue }),
            page,
            ...(activeCokitchen?.id && {
              cokitchen_id: activeCokitchen?.id
            })
          }
        });
        setDealState((prevState) => ({
          ...prevState,
          deals: page === 1 ? res.data.data : [...prevState.deals, ...res.data.data],
          totalDeals: res.data.total,
          page
        }));
      } catch (error) {
        handleApiError(error);
      } finally {
        setDealState((prevState) => ({
          ...prevState,
          loading: false
        }));
      }
    },
    [dealStatusValue, searchValue, activeCokitchen]
  );

  useEffect(() => {
    setSearchValue('');
  }, []);

  useEffect(() => {
    fetchDeals(1, true);
  }, [dealStatusValue, searchValue, activeCokitchen]);

  const handleLoadMore = () => {
    fetchDeals(dealState.page + 1);
  };

  const handleDelete = (id: string) => {
    setDealState((prevState) => ({
      ...prevState,
      deals: prevState.deals.filter((d) => d.id !== id)
    }));
  };
  return (
    <section className='p-8 '>
      <section className=''>
        <div className='flex items-center justify-between'>
          <div>
            <h2 className='text-2xl font-extrabold opacity-90'>Deal & Discount</h2>
            <p className='text-gray-8 text-sm font-medium mt-1'>
              Create & manage discounts that will be shown on the home page
            </p>
          </div>
          <Link
            to={ROUTE_KEYS.CREATE_DEAL}
            className='bg-red-5 text-white w-[139px] py-[9.5px] text-center rounded-[100px] font-medium text-sm'>
            + Add new
          </Link>
        </div>

        <div className='text-sm font-medium flex justify-between mt-10 '>
          <ul className='flex gap-4'>
            {statusArr.map((status) => (
              <li
                onClick={() => setDealStatus({ dealStatus: status.toLowerCase() })}
                key={status.toLowerCase()}
                className={`underline_animation_normal ${
                  dealStatusValue === status.toLowerCase()
                    ? 'underline_animation_selected'
                    : 'underline_animation_notSelected'
                }`}>
                <button className=''>{status}</button>
              </li>
            ))}
          </ul>
          <button
            onClick={() => setOpenPerformaceOverviewModal(true)}
            className='flex items-center gap-1 text-blue-5 font-semibold text-sm pb-4'>
            <SurgeFilledIcon />
            See performance overview
          </button>
        </div>
        <hr className=' border-gray-1/80 h-1' />
        {dealState.loading ? (
          <PageLoader />
        ) : dealState.deals && dealState.deals.length ? (
          <InfiniteScroll
            dataLength={dealState.deals.length}
            next={handleLoadMore}
            hasMore={dealState.deals.length < dealState.totalDeals}
            loader={<h4 className='text-center mt-2'>Loading...</h4>}
            endMessage={<p className='mt-2 text-center'>Yay! You have seen it all</p>}>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-6 mt-10 gap-y-10'>
              {dealState.deals.map((deal) => (
                <DealCard key={deal.id} deal={deal} handleDelete={() => handleDelete(deal.id)} />
              ))}
            </div>
          </InfiniteScroll>
        ) : (
          <div className='flex flex-col items-center justify-center min-h-[30rem]'>
            <DealsEmptyState />
            <p className='text-center text-sm font-semibold text-gray-8/70'>
              You have not created any deal & discount.
            </p>
          </div>
        )}
      </section>
      {openPerformaceOverviewModal && (
        <PerformanceOverviewModal
          isOpen={openPerformaceOverviewModal}
          onClose={() => setOpenPerformaceOverviewModal(false)}
        />
      )}
    </section>
  );
};

export default Deals;
